/* TODO: t() */
import React, { Fragment, useReducer, useMemo, useCallback } from 'react';

import { SimpleViewDispatchProvider, SimpleViewStateProvider } from './Context';
import SimpleViewPlacementModal from './SimpleViewPlacementModal';
// TODO: Check why placement is separated into PlacementEditor and VendorPlacement
import SimpleMapView from '../../VendorPlacement/SimpleMapView';

import getPercentageList from '../helpers/getPercentageList';
import {
  placeVendorToAllDates,
  placeVendorToEmptyDates,
} from '../helpers/placeVendor';

import { Market } from '../../../types/shared/Market';
import {
  VendorList,
  VendorSummaryWithMarkets,
} from '../../../types/shared/Vendor';
import { PlacementByVendor } from '../../../types/shared/Placement';
import { PositionMapByMarket } from '../../../types/shared/PositionMap';
import { Tag } from '../../../types/shared/Tag';

type Action =
  | {
      type: 'ON_MARKER_CLICK';
      markerIndex: number;
    }
  | {
      type: 'ON_VENDOR_CLICK';
      vendor: VendorSummaryWithMarkets;
    }
  | {
      type: 'ON_PLACE_SPECIFIC_DATE_CLICK';
    }
  | {
      type: 'ON_PLACE_ALL_CLICK';
    }
  | {
      type: 'ON_PLACE_ALL_CANCEL_CLICK';
    }
  | {
      type: 'DISMISS_MODAL';
    };

export type SimpleViewAction = Action;

type Props = {
  market: Market;
  placement: PlacementByVendor;
  positionMap: PositionMapByMarket;
  tags: Array<Tag>;
  vendors: VendorList;
  canEdit?: boolean | null | undefined;
};

let initialState: {
  activeIndex: number | null | undefined;
  activeVendor: VendorSummaryWithMarkets | null | undefined;
  isPlacingToSpecificDates: boolean;
  isConfirmingPlaceAllDates: boolean;
} = {
  activeIndex: null,
  activeVendor: null,
  isPlacingToSpecificDates: false,
  isConfirmingPlaceAllDates: false,
};
export type State = typeof initialState;

function reducer(state: State, action: Action) {
  switch (action.type) {
    case 'ON_MARKER_CLICK':
      return {
        ...state,
        activeIndex: action.markerIndex,
        activeVendor: null,
        isPlacingToSpecificDates: false,
      };
    case 'ON_VENDOR_CLICK':
      return {
        ...state,
        activeVendor: action.vendor,
      };
    case 'ON_PLACE_SPECIFIC_DATE_CLICK':
      return {
        ...state,
        isPlacingToSpecificDates: true,
      };
    case 'ON_PLACE_ALL_CLICK':
      return {
        ...state,
        isConfirmingPlaceAllDates: true,
      };
    case 'ON_PLACE_ALL_CANCEL_CLICK':
      return {
        ...state,
        isConfirmingPlaceAllDates: false,
      };
    case 'DISMISS_MODAL':
      return {
        ...initialState,
      };
    default:
      return state;
  }
}

export default function SimpleView(props: Props) {
  let { market, vendors, placement, positionMap, tags, canEdit } = props;

  let [state, dispatch] = useReducer(reducer, initialState);
  let { activeIndex, activeVendor } = state;

  let onMarkerClick = useCallback(
    (markerIndex: number) => dispatch({ type: 'ON_MARKER_CLICK', markerIndex }),
    [],
  );

  let marketPositionMap = useMemo(() => positionMap[market.id] || {}, [
    market.id,
    positionMap,
  ]);
  let stallDesignationName =
    marketPositionMap[String(activeIndex)] || String((activeIndex ?? 0) + 1);

  let percentageList = useMemo(
    () => getPercentageList(vendors, placement, market),
    [vendors, placement, market],
  );

  let placeToAllDates = () => {
    activeIndex != null &&
      activeVendor &&
      placeVendorToAllDates(
        market,
        vendors,
        placement,
        activeIndex,
        activeVendor,
      );
  };

  let placeToEmptyDates = () => {
    activeIndex != null &&
      activeVendor &&
      placeVendorToEmptyDates(
        market,
        vendors,
        placement,
        activeIndex,
        activeVendor,
      );
  };

  return (
    <Fragment>
      <SimpleMapView
        market={market}
        onMarkerClick={canEdit ? onMarkerClick : () => null}
        percentageList={percentageList}
        placement={placement}
        marketPositionMap={marketPositionMap}
        vendors={vendors}
      />
      {activeIndex != null && (
        <SimpleViewDispatchProvider value={dispatch}>
          <SimpleViewStateProvider
            value={{
              ...state,
              market,
              placement,
              tags,
              vendors,
            }}
          >
            <SimpleViewPlacementModal
              placeVendorToAllDates={placeToAllDates}
              placeVendorToEmptyDates={placeToEmptyDates}
              stallName={stallDesignationName}
            />
          </SimpleViewStateProvider>
        </SimpleViewDispatchProvider>
      )}
    </Fragment>
  );
}
